import * as Yup from 'yup';
import { Select, TextField, FieldData } from 'vopay';
import { useTranslation } from 'react-i18next';
import { usePaylink, Flow } from '@/providers/Paylink.provider';
import { useToken } from '@/providers/Token.provider';
import { EmailRequest } from './types';
import AccountType from './AccountType/AccountType';

export function useEmailFormRows(
  type: 'add' | 'edit',
  paylinkHasName: boolean,
  autoDepositEnabled: boolean,
): FieldData<EmailRequest>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'PaymentMethodForm' });
  const { flow } = usePaylink();
  const { token } = useToken();

  const PaymentOptions = [
    {
      label: t('phoneNumber'),
      value: 'phone',
      isVisible: flow !== Flow.FUND__SINGLE && flow !== Flow.FUND__SCHEDULED,
    },
    { label: 'Email', value: 'email', isVisible: true },
  ];

  const SecurityQuestions = [
    { label: t('securityQuestion1'), value: `What is your mother's maiden name?` },
    { label: t('securityQuestion2'), value: `What is the name of your first pet?` },
    { label: t('securityQuestion3'), value: `In what city were you born?` },
    { label: t('securityQuestion4'), value: `What is your father’s middle name?` },
    { label: t('securityQuestion5'), value: `What is the name of your childhood best friend?` },
  ];

  const shouldHaveQuestionAndAnswer = (flow === Flow.WITHDRAW__SINGLE || type === 'edit') && !autoDepositEnabled;

  return [
    {
      label: () => t('accountType'),
      fieldName: 'AccountType',
      getInputState: () => (token?.AccountHolderType === '' ? 'required' : 'disabled'),
      showField: () => !paylinkHasName,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<'personal' | 'business'>({});
        return <AccountType {...fieldData} />;
      },
    },
    {
      label: () => t('firstName'),
      fieldName: 'FirstName',
      getInputState: () => 'required',
      showField: (values) => !paylinkHasName && values.AccountType === 'personal',
      serializeField: (values) => values.FirstName,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="text" {...fieldData} />;
      },
      validate: ({ values }) =>
        values.AccountType === 'personal'
          ? Yup.mixed().required(t('valueRequired') || 'This value is required')
          : Yup.mixed(),
    },
    {
      label: () => t('lastName'),
      fieldName: 'LastName',
      getInputState: () => 'required',
      showField: (values) => !paylinkHasName && values.AccountType === 'personal',
      serializeField: (values) => values.LastName,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="text" {...fieldData} />;
      },
      validate: ({ values }) =>
        values.AccountType === 'personal'
          ? Yup.mixed().required(t('valueRequired') || 'This value is required')
          : Yup.mixed(),
    },
    {
      label: () => t('companyName'),
      fieldName: 'CompanyName',
      getInputState: () => 'required',
      showField: (values) => !paylinkHasName && values.AccountType === 'business',
      serializeField: (values) => values.CompanyName,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="text" {...fieldData} />;
      },
      validate: ({ values }) =>
        values.AccountType === 'business'
          ? Yup.mixed().required(t('valueRequired') || 'This value is required')
          : Yup.mixed(),
    },
    {
      label: () => t('paymentMethod'),
      fieldName: 'PaymentMethod',
      getInputState: () => 'required',
      serializeField: (values: EmailRequest) => values.PaymentMethod,
      showField: () => type === 'add',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder=""
            options={PaymentOptions.filter((option) => option.isVisible)}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(t('valueRequired') || 'This value is required'),
    },
    {
      label: () => t('providePhoneNumber'),
      fieldName: 'PhoneNumber',
      getInputState: () => 'required',
      showField: (values: EmailRequest) => type === 'add' && values.PaymentMethod === 'phone',
      serializeField: (values: EmailRequest) =>
        type === 'add' && values.PaymentMethod === 'phone' ? values.PhoneNumber : '',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="text" placeholder={t('enter') || ''} {...fieldData} />;
      },
      validate: () => Yup.string().required(t('valueRequired') || 'This value is required'),
    },
    {
      label: () => t('provideEmail'),
      fieldName: 'EmailAddress',
      getInputState: () => 'required',
      showField: (values: EmailRequest) => type === 'add' && values.PaymentMethod === 'email',
      serializeField: (values: EmailRequest) =>
        type === 'add' && values.PaymentMethod === 'email' ? values.EmailAddress : '',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="text" placeholder={t('enter') || ''} {...fieldData} />;
      },
      validate: () =>
        Yup.string()
          .email()
          .required(t('valueRequired') || 'This value is required'),
    },
    {
      label: () => t('securityQuestion'),
      fieldName: 'Question',
      getInputState: () => 'required',
      showField: () => shouldHaveQuestionAndAnswer,
      serializeField: (values: EmailRequest) => (shouldHaveQuestionAndAnswer ? values.Question : ''),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <Select version="2" placeholder="" options={SecurityQuestions} sort={false} {...fieldData} />;
      },
      validate: () =>
        shouldHaveQuestionAndAnswer
          ? Yup.string().required(t('valueRequired') || 'This value is required')
          : Yup.string(),
    },
    {
      label: () => t('securityAnswer'),
      fieldName: 'Answer',
      getInputState: () => 'required',
      showField: () => shouldHaveQuestionAndAnswer,
      serializeField: (values: EmailRequest) => (shouldHaveQuestionAndAnswer ? values.Answer : ''),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="text" placeholder={`${t('enter')}`} {...fieldData} />;
      },
      validate: () =>
        shouldHaveQuestionAndAnswer
          ? Yup.string().required(t('valueRequired') || 'This value is required')
          : Yup.string(),
    },
  ];
}
